'use client';

import { ChartContainer } from '@/components/ui/chart';

import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

type Format =
  | 'number'
  | 'percent'
  | 'usd'
  | 'usdM'
  | 'usdB'
  | 'usdT'
  | 'multiple'
  | 'points';

interface Series {
  key: string;
  name?: string;
  color?: string;
  /** Mark type for this series; defaults to the chart kind. */
  kind?: 'line' | 'area' | 'bar';
  /** Plot against the right-hand axis (dual-axis combo charts). */
  axis?: 'left' | 'right';
  dashed?: boolean;
}

interface Annotation {
  x: string | number;
  label: string;
}

export interface StoryChartProps {
  /** `hbar` ranks categories. */
  kind?: 'line' | 'area' | 'bar' | 'stacked' | 'hbar';
  data: Record<string, string | number | null>[];
  x: string;
  series?: Series[];
  /** The insight the figure proves, written as a sentence. */
  headline: string;
  /** What is plotted, with units. */
  subtitle?: string;
  source: string;
  sourceHref?: string;
  note?: string;
  annotations?: Annotation[];
  /** For bar charts: categories to emphasise; the rest are muted. */
  highlight?: (string | number)[];
  format?: Format;
  rightFormat?: Format;
  logY?: boolean;
  height?: number;
}

const PALETTE = [
  '#0f9d7a',
  '#d97706',
  '#3b82f6',
  '#8b5cf6',
  '#E55B3F',
  '#64748b'
];
const MUTED = '#94a3b8';

const compact = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${+(n / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${+(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${+(n / 1e3).toFixed(1)}K`;
  return `${+n.toFixed(abs < 10 ? 2 : 1)}`;
};

const format = (value: unknown, kind: Format = 'number') => {
  if (typeof value !== 'number')
    return value === null || value === undefined ? '—' : String(value);
  switch (kind) {
    case 'percent':
      return `${+value.toFixed(1)}%`;
    case 'usd':
      return `$${compact(value)}`;
    case 'usdM':
      return `$${+value.toFixed(1)}M`;
    case 'usdB':
      return `$${+value.toFixed(1)}B`;
    case 'usdT':
      return `$${+value.toFixed(2)}T`;
    case 'multiple':
      return `${+value.toFixed(1)}x`;
    case 'points':
      return `${value > 0 ? '+' : ''}${+value.toFixed(1)} pts`;
    default:
      return compact(value);
  }
};

export function StoryChart({
  kind = 'line',
  data,
  x,
  series = [],
  headline,
  subtitle,
  source,
  sourceHref,
  note,
  annotations = [],
  highlight,
  format: yFormat = 'number',
  rightFormat,
  logY,
  height = 380
}: StoryChartProps) {
  const colored = series.map((s, i) => ({
    ...s,
    color: s.color ?? PALETTE[i % PALETTE.length]
  }));
  const isHorizontal = kind === 'hbar';
  const hasRightAxis = colored.some((s) => s.axis === 'right');
  const markFor = (s: (typeof colored)[number]) =>
    s.kind ?? (kind === 'stacked' ? 'bar' : kind === 'hbar' ? 'bar' : kind);
  const allLines = colored.every((s) => markFor(s) !== 'bar');
  // Direct end labels collide beyond three series, so larger sets get a legend instead
  const directLabels =
    colored.length > 1 && colored.length <= 3 && allLines && !isHorizontal;
  const showLegend = colored.length > 1 && !directLabels && !isHorizontal;
  const chartHeight = isHorizontal
    ? Math.max(height, data.length * 34 + 60)
    : height;

  const tooltip = (
    <Tooltip
      cursor={{ stroke: MUTED, strokeDasharray: '3 3' }}
      content={({ active, payload, label: tipLabel }) => {
        if (!active || !payload?.length) return null;
        const row = payload[0].payload as Record<string, unknown>;
        return (
          <div className='border-border bg-background space-y-1 rounded-lg border px-3 py-2 text-xs shadow-xl'>
            <p className='font-bold'>{String(tipLabel)}</p>
            {payload.map((p) => {
              const s = colored.find((c) => c.key === p.dataKey);
              return (
                <p
                  key={String(p.dataKey)}
                  className='flex items-center gap-2 font-mono'>
                  <span
                    className='size-2 rounded-full'
                    style={{ backgroundColor: s?.color ?? MUTED }}
                  />
                  {s?.name ?? String(p.dataKey)}:{' '}
                  {format(p.value, s?.axis === 'right' ? rightFormat : yFormat)}
                </p>
              );
            })}
          </div>
        );
      }}
    />
  );

  const columns = data.length ? Object.keys(data[0]) : [];

  return (
    <figure className='border-border bg-card my-10 w-full rounded-2xl border p-5 shadow-sm sm:p-7'>
      <figcaption className='mb-5'>
        <p className='text-foreground font-serif text-xl leading-snug font-bold sm:text-2xl'>
          {headline}
        </p>
        {subtitle && (
          <p className='text-muted-foreground mt-1.5 text-sm'>{subtitle}</p>
        )}
      </figcaption>

      <div
        role='img'
        aria-label={`${headline}. ${subtitle ?? ''}`}
        className='text-muted-foreground w-full overflow-x-auto'>
        <div style={{ minWidth: 280 }}>
          <ChartContainer
            config={{}}
            style={{ height: chartHeight, minHeight: chartHeight }}>
            <ComposedChart
              data={data}
              layout={isHorizontal ? 'vertical' : 'horizontal'}
              margin={{
                top: 22,
                right: directLabels ? 96 : 24,
                left: 4,
                bottom: 8
              }}>
              <defs>
                {colored.map((s) => (
                  <linearGradient
                    key={s.key}
                    id={`sc-${s.key}`}
                    x1='0'
                    y1='0'
                    x2='0'
                    y2='1'>
                    <stop
                      offset='0%'
                      stopColor={s.color}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset='100%'
                      stopColor={s.color}
                      stopOpacity={0.03}
                    />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                stroke='currentColor'
                strokeOpacity={0.12}
                vertical={isHorizontal}
                horizontal={!isHorizontal}
              />
              {isHorizontal ? (
                <>
                  <XAxis
                    type='number'
                    domain={[(min: number) => Math.min(0, min), 'auto']}
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    tickFormatter={(v) => format(v, yFormat)}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    type='category'
                    dataKey={x}
                    width={150}
                    interval={0}
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                </>
              ) : (
                <>
                  <XAxis
                    dataKey={x}
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    tickLine={false}
                    stroke='currentColor'
                    strokeOpacity={0.3}
                    minTickGap={16}
                  />
                  <YAxis
                    yAxisId='left'
                    scale={logY ? 'log' : 'auto'}
                    domain={logY ? ['auto', 'auto'] : [0, 'auto']}
                    allowDataOverflow={Boolean(logY)}
                    tick={{ fill: 'currentColor', fontSize: 12 }}
                    tickFormatter={(v) => format(v, yFormat)}
                    tickLine={false}
                    axisLine={false}
                    width={56}
                  />
                  {hasRightAxis && (
                    <YAxis
                      yAxisId='right'
                      orientation='right'
                      tick={{ fill: 'currentColor', fontSize: 12 }}
                      tickFormatter={(v) => format(v, rightFormat ?? yFormat)}
                      tickLine={false}
                      axisLine={false}
                      width={56}
                    />
                  )}
                </>
              )}
              {tooltip}

              {annotations.map((a, i) => (
                <ReferenceLine
                  key={String(a.x)}
                  {...(isHorizontal ? { y: a.x } : { x: a.x, yAxisId: 'left' })}
                  stroke='currentColor'
                  strokeOpacity={0.45}
                  strokeDasharray='4 4'
                  label={{
                    value: String(i + 1),
                    position: 'top',
                    fill: 'currentColor',
                    fontSize: 12,
                    fontWeight: 700
                  }}
                />
              ))}

              {colored.map((s) => {
                const mark = markFor(s);
                const common = {
                  dataKey: s.key,
                  name: s.name ?? s.key,
                  yAxisId: isHorizontal ? undefined : (s.axis ?? 'left')
                };
                if (mark === 'bar') {
                  return (
                    <Bar
                      key={s.key}
                      {...common}
                      fill={s.color}
                      stackId={kind === 'stacked' ? 'a' : undefined}
                      radius={
                        isHorizontal
                          ? [0, 6, 6, 0]
                          : kind === 'stacked'
                            ? 0
                            : [5, 5, 0, 0]
                      }
                      maxBarSize={isHorizontal ? 20 : 48}
                      isAnimationActive={false}>
                      {highlight &&
                        data.map((row) => (
                          <Cell
                            key={String(row[x])}
                            fill={
                              highlight.includes(row[x] as string | number)
                                ? s.color
                                : MUTED
                            }
                            fillOpacity={
                              highlight.includes(row[x] as string | number)
                                ? 1
                                : 0.55
                            }
                          />
                        ))}
                      {isHorizontal && (
                        <LabelList
                          dataKey={s.key}
                          position='right'
                          formatter={(v: unknown) => format(v, yFormat)}
                          style={{
                            fill: 'currentColor',
                            fontSize: 12,
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Bar>
                  );
                }
                const endLabel = directLabels
                  ? (p: { index?: number; x?: number; y?: number }) =>
                      p.index === data.length - 1 ? (
                        <text
                          x={(p.x ?? 0) + 8}
                          y={(p.y ?? 0) + 4}
                          fill={s.color}
                          fontSize={12}
                          fontWeight={700}>
                          {s.name ?? s.key}
                        </text>
                      ) : (
                        <g />
                      )
                  : undefined;
                return mark === 'area' ? (
                  <Area
                    key={s.key}
                    {...common}
                    type='monotone'
                    stroke={s.color}
                    strokeWidth={2.5}
                    fill={`url(#sc-${s.key})`}
                    dot={false}
                    label={endLabel as never}
                    isAnimationActive={false}
                  />
                ) : (
                  <Line
                    key={s.key}
                    {...common}
                    type='monotone'
                    stroke={s.color}
                    strokeWidth={2.75}
                    strokeDasharray={s.dashed ? '6 5' : undefined}
                    dot={
                      data.length <= 14
                        ? { r: 3.5, fill: s.color, strokeWidth: 0 }
                        : false
                    }
                    activeDot={{ r: 5.5 }}
                    label={endLabel as never}
                    isAnimationActive={false}
                  />
                );
              })}
            </ComposedChart>
          </ChartContainer>
        </div>
      </div>

      {annotations.length > 0 && (
        <ol className='text-muted-foreground mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs'>
          {annotations.map((a, i) => (
            <li
              key={String(a.x)}
              className='flex items-center gap-1.5'>
              <span className='bg-foreground/80 text-background flex size-4 items-center justify-center rounded-full text-[10px] font-bold'>
                {i + 1}
              </span>
              <span>
                {String(a.x)}: {a.label}
              </span>
            </li>
          ))}
        </ol>
      )}

      {showLegend && (
        <div className='mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs'>
          {colored.map((s) => (
            <span
              key={s.key}
              className='text-muted-foreground flex items-center gap-1.5'>
              <span
                className='size-2.5 rounded-sm'
                style={{ backgroundColor: s.color }}
              />
              {s.name ?? s.key}
            </span>
          ))}
        </div>
      )}

      <p className='text-muted-foreground mt-4 text-xs leading-relaxed'>
        Source:{' '}
        {sourceHref ? (
          <a
            href={sourceHref}
            target='_blank'
            rel='noopener noreferrer'
            className='underline'>
            {source}
          </a>
        ) : (
          source
        )}
        .{note ? ` ${note}` : ''}
      </p>

      <details className='text-muted-foreground mt-3 text-xs'>
        <summary className='hover:text-foreground cursor-pointer font-semibold'>
          View data
        </summary>
        <div className='mt-2 overflow-x-auto'>
          <table className='w-full min-w-[320px] border-collapse text-left'>
            <thead>
              <tr className='border-border border-b'>
                {columns.map((c) => (
                  <th
                    key={c}
                    scope='col'
                    className='py-1.5 pr-4 font-bold'>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr
                  key={i}
                  className='border-border/40 border-b'>
                  {columns.map((c) => (
                    <td
                      key={c}
                      className='py-1.5 pr-4 font-mono'>
                      {row[c] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

/** A clearly marked first-person opinion block that closes each post. */
export function MyRead({ children }: { children: React.ReactNode }) {
  return (
    <aside className='my-12 rounded-2xl border-l-4 border-emerald-600 bg-emerald-600/5 p-6 sm:p-8'>
      <p className='mb-3 text-xs font-black tracking-widest text-emerald-700 uppercase dark:text-emerald-400'>
        My read · opinion
      </p>
      <div className='[&_p]:text-foreground/90 [&_p]:mb-4 [&_p]:font-serif [&_p]:text-lg [&_p]:leading-relaxed [&_p:last-child]:mb-0'>
        {children}
      </div>
    </aside>
  );
}
